import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useI18n, useT } from "~/i18n";
import { TaxApiError, failureKindFor } from "~/lib/api/errors";
import { adviceForFailure } from "~/lib/api/failure";
import { Skeleton } from "./primitives";
import { StateArt, type StateArtKind } from "./state-art";

export type StateKind = "empty" | "denied" | "disabled" | "unavailable" | "error";

const GLYPHS: Record<StateKind, string> = {
  empty: "∅",
  denied: "⊘",
  disabled: "⊘",
  unavailable: "⚠",
  error: "!",
};

/**
 * The three negative states are visually and structurally distinct:
 *
 *   denied      lock glyph in critical ink, names the permission,
 *               offers "request access" — nothing to retry.
 *   disabled    dashed hairline, no-entry glyph, explains the *record*
 *               condition that must change first.
 *   unavailable warning glyph in caution ink, explains the dependency
 *               and offers a retry.
 *   error       bang glyph and title in critical ink on paper.
 *
 * They never share a component variant by accident: `kind` is required.
 * Colour never fills the block; glyph and text carry the tone.
 */
export function StateBlock({
  kind,
  title,
  body,
  actions,
  art,
}: {
  kind: StateKind;
  title: ReactNode;
  body?: ReactNode;
  actions?: ReactNode;
  /** Optional drawing from the art family; the text still carries the state. */
  art?: StateArtKind;
}) {
  return (
    <div
      className="state-block"
      data-kind={kind}
      data-art={art}
      role={kind === "error" ? "alert" : undefined}
    >
      {art && <StateArt kind={art} />}
      <p className="state-block__title mb-0">
        <span className="state-block__glyph" aria-hidden="true">
          {GLYPHS[kind]}
        </span>
        {title}
      </p>
      {body && <div className="state-block__body">{body}</div>}
      {actions && <div className="row mt-2">{actions}</div>}
    </div>
  );
}

/**
 * Four empty situations that must never look alike:
 *
 *   first-use        nothing exists yet — explain and offer the first action.
 *   no-results       filters hide everything — neutral, offer "Limpar filtros".
 *   nothing-pending  the queue is clear — short, no celebration.
 *   blocked          a dependency stops the operation — say which one.
 *
 * `onClear` or no title (the default title is the filtered one) implies
 * no-results; otherwise the default is first-use.
 */
export type EmptyVariant = "first-use" | "no-results" | "nothing-pending" | "blocked";

export function EmptyState({
  onClear,
  title,
  body,
  actions,
  variant,
}: {
  onClear?: () => void;
  title?: ReactNode;
  body?: ReactNode;
  actions?: ReactNode;
  variant?: EmptyVariant;
}) {
  const t = useT();
  const resolved: EmptyVariant = variant ?? (onClear || !title ? "no-results" : "first-use");
  return (
    <StateBlock
      kind="empty"
      art={resolved}
      title={title ?? t("state.empty.title")}
      body={body ?? t("state.empty.body")}
      actions={
        actions ??
        (onClear ? (
          <button type="button" className="btn btn--sm" onClick={onClear}>
            {t("common.clearFilters")}
          </button>
        ) : undefined)
      }
    />
  );
}

export function DeniedState({ permission, detail }: { permission?: string; detail?: string }) {
  const t = useT();
  return (
    <StateBlock
      kind="denied"
      title={t("access.denied.title")}
      body={
        detail ? (
          <>
            <p className="mb-1">{detail}</p>
            {permission && (
              <p className="mb-0 text-2xs text-muted">{t("access.denied.short", { permission })}</p>
            )}
          </>
        ) : (
          t("access.denied.body", { permission: permission ?? "—" })
        )
      }
      actions={
        <a
          className="btn btn--sm"
          href={`mailto:tax-platform@backchannel.com.br?subject=${encodeURIComponent(
            `Solicitação de acesso: ${permission ?? t("access.denied.title")}`,
          )}`}
        >
          {t("access.requestAccess")}
        </a>
      }
    />
  );
}

/**
 * Cognito and Tax authorization are intentionally separate. This state tells
 * an authenticated person that the identity step succeeded and only the
 * environment's explicit operator enrollment is missing.
 */
export function AccessPendingState() {
  const t = useT();
  return (
    <StateBlock
      kind="denied"
      title={t("auth.accessPending.title")}
      body={t("auth.accessPending.body")}
      actions={
        <a
          className="btn btn--sm"
          href={`mailto:tax-platform@backchannel.com.br?subject=${encodeURIComponent(
            t("auth.accessPending.title"),
          )}`}
        >
          {t("auth.accessPending.request")}
        </a>
      }
    />
  );
}

export function UnavailableState({ reason, onRetry }: { reason: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <StateBlock
      kind="unavailable"
      title={t("access.unavailable.title")}
      body={t("access.unavailable.body", { reason })}
      actions={
        onRetry && (
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            {t("common.retry")}
          </button>
        )
      }
    />
  );
}

export function DisabledState({ reason }: { reason: string }) {
  const t = useT();
  return (
    <StateBlock kind="disabled" art="blocked" title={t("access.disabled.title")} body={reason} />
  );
}

/**
 * Routes an unknown failure to the right presentation. A 403 is not "an
 * error" to an operator — it is an answer.
 */
export function QueryErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const kind = failureKindFor(error);
  const advice = adviceForFailure(error);
  const detail = `${t(advice.whatKey)} ${t(advice.todoKey)}`;

  if (kind === "denied") {
    const problemDetail =
      error instanceof TaxApiError ? (error.problem?.detail ?? error.message) : undefined;
    if (problemDetail?.includes("not registered as an active tax operator")) {
      return <AccessPendingState />;
    }
    const permission = problemDetail ? extractPermission(problemDetail) : null;
    return (
      <DeniedState
        permission={permission ?? undefined}
        detail={permission ? undefined : problemDetail}
      />
    );
  }

  if (kind === "unavailable") {
    return <UnavailableState reason={detail} onRetry={onRetry} />;
  }

  return (
    <StateBlock
      kind="error"
      title={t("state.error.title")}
      body={
        <>
          <p className="mb-2">{detail}</p>
          {error instanceof TaxApiError && error.correlationId && (
            <p className="text-2xs text-muted mono mb-0">
              {t("failure.supportId", { id: error.correlationId })}
            </p>
          )}
        </>
      }
      actions={
        (onRetry || advice.maybeCommitted) && (
          <>
            {onRetry && !advice.maybeCommitted && (
              <button type="button" className="btn btn--sm" onClick={onRetry}>
                {t("common.retry")}
              </button>
            )}
            {advice.maybeCommitted && (
              <Link className="btn btn--sm" to="/lots">
                {t("failure.openLots")}
              </Link>
            )}
          </>
        )
      }
    />
  );
}

function extractPermission(detail: string): string | null {
  const match = /([A-Z]+:[A-Z_]+:[A-Z]+)/.exec(detail);
  return match?.[1] ?? null;
}

export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "caution" | "critical" | "positive";
  children: ReactNode;
}) {
  const glyph =
    tone === "critical" ? "✕" : tone === "caution" ? "⚠" : tone === "positive" ? "✓" : "ℹ";
  return (
    <p className="notice mb-0" data-tone={tone}>
      <span aria-hidden="true">{glyph}</span>
      <span>{children}</span>
    </p>
  );
}

export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  const { t } = useI18n();
  return (
    <output aria-busy="true" aria-label={t("state.loading.label")} className="stack-2 panel__body">
      {Array.from({ length: rows }, (_, rowIndex) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative placeholder grid — cells carry no identity and are never reordered.
        <div className="row" key={`row-${rowIndex}`}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative placeholder grid — cells carry no identity and are never reordered.
              key={`cell-${rowIndex}-${columnIndex}`}
              width={columnIndex === 0 ? "34%" : "12%"}
            />
          ))}
        </div>
      ))}
    </output>
  );
}

/**
 * Loading geometry for the Overview home: calculator, rule count, recent
 * lots and new rules in the same grid as the live cards, so hydration does
 * not reshuffle the operator's scan path.
 */
export function OverviewSkeleton() {
  const { t } = useI18n();
  return (
    <output aria-busy="true" aria-label={t("state.loading.label")} className="overview-home">
      {(["cta", "rules", "jobs", "new"] as const).map((slot) => (
        <div className={`home-card home-card--skeleton home-${slot}`} key={slot} aria-hidden="true">
          <Skeleton height={11} width="28%" />
          <Skeleton height={slot === "rules" ? 34 : 22} width={slot === "rules" ? "40%" : "62%"} />
          <Skeleton height={12} width="78%" />
          {slot === "cta" ? (
            <div className="home-card--skeleton__row">
              <Skeleton height={40} width="160px" />
              <Skeleton height={40} width="160px" />
            </div>
          ) : (
            <>
              <Skeleton height={12} width="64%" />
              <Skeleton height={12} width="52%" />
            </>
          )}
        </div>
      ))}
    </output>
  );
}

/**
 * Loading geometry for rule coverage: the fiscal ledger beside the Brazil
 * map, recognizable while the read model resolves.
 */
export function CoverageSkeleton() {
  const { t } = useI18n();
  return (
    <output aria-busy="true" aria-label={t("state.loading.label")} className="overview-skeleton">
      <div className="overview-skeleton__briefing" aria-hidden="true">
        <div className="overview-skeleton__briefing-lead">
          <div className="overview-skeleton__briefing-copy">
            <Skeleton height={11} width="22%" />
            <Skeleton height={22} width="58%" />
            <Skeleton height={12} width="74%" />
          </div>
          <Skeleton height={22} width="88px" />
        </div>
        <div className="overview-skeleton__signals">
          {Array.from({ length: 8 }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative ledger placeholders — slots are never reordered.
            <div className="overview-skeleton__signal" key={`overview-signal-${index}`}>
              <Skeleton height={36} width="36px" />
              <div className="overview-skeleton__signal-copy">
                <Skeleton height={12} width={index % 2 === 0 ? "48%" : "36%"} />
                <Skeleton height={10} width="70%" />
              </div>
              <Skeleton height={16} width="52px" />
            </div>
          ))}
        </div>
      </div>
      <div className="overview-skeleton__map" aria-hidden="true">
        <Skeleton height={15} width="28%" />
        <Skeleton height={12} width="46%" />
        <Skeleton height={320} width="100%" />
      </div>
    </output>
  );
}

/**
 * Loading geometry for the evidence ledger. Rows, not cards, so the first
 * paint does not jump from a grid of posters into a scan list.
 */
export function EvidenceCatalogueSkeleton({ cards = 6 }: { cards?: number }) {
  const { t } = useI18n();
  return (
    <output
      aria-busy="true"
      aria-label={t("state.loading.label")}
      className="evidence-catalogue evidence-catalogue--skeleton"
    >
      {Array.from({ length: cards }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative row placeholders — slots are never reordered.
        <div className="evidence-card evidence-card--skeleton" key={`evidence-card-${index}`}>
          <Skeleton height={18} width="18px" />
          <Skeleton height={36} width="36px" />
          <div className="evidence-card__copy" aria-hidden="true">
            <Skeleton height={14} width="72%" />
            <Skeleton height={11} width="48%" />
          </div>
        </div>
      ))}
    </output>
  );
}

/** Loading geometry for source health rows. */
export function SourceListSkeleton({ cards = 6 }: { cards?: number }) {
  const { t } = useI18n();
  return (
    <output
      aria-busy="true"
      aria-label={t("state.loading.label")}
      className="source-catalogue source-catalogue--skeleton"
    >
      {Array.from({ length: cards }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative row placeholders — slots are never reordered.
        <article className="source-row source-row--skeleton" key={`source-card-${index}`}>
          <Skeleton height={36} width="36px" />
          <div className="source-row__copy" aria-hidden="true">
            <Skeleton height={14} width="220px" />
            <Skeleton height={11} width="180px" />
          </div>
          <div className="source-row__facts" aria-hidden="true">
            <Skeleton height={18} width="52px" />
            <Skeleton height={14} width="40px" />
          </div>
          <div className="source-row__actions" aria-hidden="true">
            <Skeleton height={18} width="76px" />
            <Skeleton height={18} width="64px" />
          </div>
        </article>
      ))}
    </output>
  );
}

/**
 * Geometry-matched loading state for the evidence intelligence workbench.
 * Keeping the three regions visible while data is fetched prevents the detail
 * page from jumping from a table-shaped placeholder into its final layout.
 */
export function EvidenceDetailSkeleton() {
  const { t } = useI18n();
  return (
    <output
      aria-busy="true"
      aria-label={t("state.loading.label")}
      className="evidence-detail-skeleton"
    >
      <div className="evidence-detail-skeleton__heading" aria-hidden="true">
        <Skeleton height={22} width="34%" />
        <Skeleton height={14} width="52%" />
      </div>

      <div className="evidence-detail-skeleton__workbench" aria-hidden="true">
        <div className="evidence-detail-skeleton__panel evidence-detail-skeleton__panel--facts">
          <Skeleton height={16} width="58%" />
          <Skeleton height={36} width="42%" />
          <div className="evidence-detail-skeleton__lines">
            <Skeleton height={12} width="82%" />
            <Skeleton height={12} width="68%" />
            <Skeleton height={12} width="76%" />
            <Skeleton height={12} width="62%" />
          </div>
        </div>

        <div className="evidence-detail-skeleton__panel evidence-detail-skeleton__panel--article">
          <div className="evidence-detail-skeleton__source">
            <Skeleton height={38} width="38px" />
            <div className="evidence-detail-skeleton__source-copy">
              <Skeleton height={13} width="62%" />
              <Skeleton height={11} width="48%" />
            </div>
            <Skeleton height={22} width="110px" />
          </div>
          <Skeleton height={170} width="100%" />
          <div className="evidence-detail-skeleton__copy">
            <Skeleton height={12} width="26%" />
            <Skeleton height={14} width="92%" />
            <Skeleton height={14} width="84%" />
            <Skeleton height={14} width="72%" />
          </div>
        </div>

        <div className="evidence-detail-skeleton__panel evidence-detail-skeleton__panel--insights">
          <Skeleton height={16} width="52%" />
          <div className="evidence-detail-skeleton__insights">
            <Skeleton height={64} width="100%" />
            <Skeleton height={64} width="100%" />
            <Skeleton height={64} width="100%" />
          </div>
        </div>
      </div>
    </output>
  );
}
